import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useState } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Pill,
  Activity,
  Droplets,
  Utensils,
  AlertCircle,
  ArrowLeft,
  Save,
  Clock,
  Dumbbell,
  AlertTriangle
} from 'lucide-react';
import type { CarePlanSuggestions, MedicationSuggestion, RoutineSuggestion } from '@/pages/care-plan/AIWizard';

interface AISuggestionReviewProps {
  suggestions: CarePlanSuggestions;
  onUpdate: (updated: CarePlanSuggestions) => void;
  onSave: (action: 'add' | 'replace') => void;
  onBack: () => void;
}

export function AISuggestionReview({
  suggestions,
  onUpdate,
  onSave,
  onBack
}: AISuggestionReviewProps) {
  const { t } = useTranslation();
  const [saveAction, setSaveAction] = useState<'add' | 'replace'>('add');

  const toggleMedication = (index: number) => {
    const updated = { ...suggestions };
    updated.medications = suggestions.medications.map((m, i) =>
      i === index ? { ...m, accepted: !m.accepted } : m
    );
    onUpdate(updated);
  };

  const toggleRoutine = (index: number) => {
    const updated = { ...suggestions };
    updated.suggested_routines = suggestions.suggested_routines.map((r, i) =>
      i === index ? { ...r, accepted: !r.accepted } : r
    );
    onUpdate(updated);
  };

  const getRoutineIcon = (type: string) => {
    if (type === 'physical_activity') {
      return <Dumbbell className="w-4 h-4 text-success" />;
    }
    switch (type) {
      case 'vital_sign': return <Activity className="w-4 h-4 text-info" />;
      case 'hygiene': return <Droplets className="w-4 h-4 text-primary" />;
      case 'food': return <Utensils className="w-4 h-4 text-warning" />;
      default: return <AlertCircle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getMobilityLabel = (mobility: string) => {
    switch (mobility) {
      case 'independent': return t('aiWizard.mobilityIndependent');
      case 'needs_support': return t('aiWizard.mobilityNeedsSupport');
      case 'wheelchair': return t('aiWizard.mobilityWheelchair');
      case 'bedridden': return t('aiWizard.mobilityBedridden');
      default: return mobility;
    }
  };

  const acceptedCount =
    suggestions.medications.filter(m => m.accepted).length +
    suggestions.suggested_routines.filter(r => r.accepted).length;

  return (
    <div className="space-y-4">
      {/* Save Action Selection - Moved to Top for Visibility */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-4 space-y-4">
          <div className="space-y-3">
            <Label className="text-base font-semibold text-primary flex items-center gap-2">
              <Save className="w-4 h-4" />
              {t('aiWizard.saveAction')}
            </Label>
            <RadioGroup
              value={saveAction}
              onValueChange={(v) => setSaveAction(v as 'add' | 'replace')}
              className="flex flex-col gap-3"
            >
              <div className="flex items-center space-x-2 bg-background p-3 rounded-lg border cursor-pointer hover:bg-accent transition-colors">
                <RadioGroupItem value="add" id="add" />
                <Label htmlFor="add" className="font-medium cursor-pointer flex-1">
                  {t('aiWizard.addAction')}
                </Label>
              </div>
              <div className="flex items-center space-x-2 bg-background p-3 rounded-lg border cursor-pointer hover:bg-accent transition-colors">
                <RadioGroupItem value="replace" id="replace" />
                <Label htmlFor="replace" className="font-medium cursor-pointer flex-1">
                  {t('aiWizard.replaceAction')}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {saveAction === 'replace' && (
            <div className="bg-destructive/10 text-destructive text-xs p-3 rounded-lg flex items-start gap-2 border border-destructive/20 animate-in fade-in slide-in-from-top-1">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="font-medium">{t('aiWizard.replaceWarning')}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Medications */}
      {suggestions.medications.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Pill className="w-4 h-4 text-primary" />
              {t('aiWizard.medications')} ({suggestions.medications.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {suggestions.medications.map((med, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border transition-colors ${med.accepted ? 'bg-primary/5 border-primary/20' : 'bg-muted/50 opacity-60'
                  }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={med.accepted}
                    onCheckedChange={() => toggleMedication(index)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{med.name}</span>
                      {med.is_sporadic && (
                        <Badge variant="secondary" className="text-[12px]">SOS</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {med.dose} {med.dose_unit} - {med.administration_route}
                    </p>
                    {med.schedule_times.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {med.schedule_times.join(', ')}
                      </div>
                    )}
                    {med.notes && (
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        {med.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Routines */}
      {suggestions.suggested_routines.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-info" />
              {t('aiWizard.routines')} ({suggestions.suggested_routines.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {suggestions.suggested_routines.map((routine, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border transition-colors ${routine.accepted ? 'bg-primary/5 border-primary/20' : 'bg-muted/50 opacity-60'
                  }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={routine.accepted}
                    onCheckedChange={() => toggleRoutine(index)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {getRoutineIcon(routine.type)}
                      <span className="font-medium">{routine.name}</span>
                    </div>
                    {routine.schedule_times.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {routine.schedule_times.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Dietary Restrictions */}
      {suggestions.dietary_restrictions.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Utensils className="w-4 h-4 text-warning" />
              {t('aiWizard.dietaryRestrictions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {suggestions.dietary_restrictions.map((restriction, index) => (
                <Badge key={index} variant="outline" className="text-[12px]">
                  {restriction}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mobility & Notes */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div>
            <span className="text-sm font-medium">{t('aiWizard.mobility')}: </span>
            <Badge variant="secondary" className="text-[12px]">{getMobilityLabel(suggestions.mobility_needs)}</Badge>
          </div>
          {suggestions.additional_notes && (
            <>
              <Separator />
              <div>
                <span className="text-sm font-medium">{t('aiWizard.additionalNotes')}: </span>
                <p className="text-sm text-muted-foreground mt-1">
                  {suggestions.additional_notes}
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>


      {/* Action Buttons */}
      <div className="flex gap-3 pt-2">
        <Button variant="outline" className="flex-1 gap-2" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>
        <Button
          className="flex-1 gap-2"
          onClick={() => onSave(saveAction)}
          disabled={acceptedCount === 0}
        >
          <Save className="w-4 h-4" />
          {t('aiWizard.saveItems', { count: acceptedCount })}
        </Button>
      </div>
    </div>
  );
}
